import React, { useEffect, useMemo, useState } from "react";
import { formatARS } from "../../utils/currency";

const plansFor = (product) => [
  { installments: 1, label: "Contado", total: product?.cash_price },
  { installments: 3, label: "Oxígeno", total: product?.oxigeno_installments_1 },
  { installments: 6, label: "6 cuotas", total: product?.installments_6 },
  { installments: 9, label: "9 cuotas", total: product?.installments_9 },
  { installments: 10, label: "10 cuotas", total: product?.installments_10 },
  { installments: 12, label: "12 cuotas", total: product?.installments_12 },
  { installments: 14, label: "14 cuotas", total: product?.installments_14 },
].filter((plan) => plan.total != null && Number(plan.total) > 0);

const TmcApplicationForm = ({ onOpen, onClose, onSubmit, userName, products = [] }) => {
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [details, setDetails] = useState("");

  useEffect(() => {
    if (onOpen) {
      setSearch("");
      setSelectedProduct(null);
      setDetails("");
    }
  }, [onOpen]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((product) => {
      const totalStock = (product.stockDeposits || []).reduce(
        (sum, deposit) => sum + Number(deposit.quantity || 0),
        0
      );
      return totalStock > 0 && (!term ||
        String(product.description || "").toLowerCase().includes(term) ||
        String(product.code || "").toLowerCase().includes(term));
    });
  }, [products, search]);

  if (!onOpen) return null;

  const submit = (event) => {
    event.preventDefault();
    if (!selectedProduct) return;
    onSubmit?.({
      details: details.trim(),
      stockId: selectedProduct.id,
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-6xl h-[90vh] max-h-[90vh] p-6 flex flex-col overflow-hidden">
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Nueva solicitud de compra TMC</h2>
            <p className="text-sm text-slate-500">Solicitante: {userName}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800 font-bold">Cerrar</button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-4 pr-2">
          <div className="flex flex-col gap-4">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por producto o código..."
              className="w-full flex-shrink-0 border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-custom-green-ccard-1"
            />

            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">Los valores publicados corresponden al precio al público.</p>
              <p>El descuento especial para empleados y las cuotas finales se definirán durante la aprobación sobre el valor de contado.</p>
            </div>

            <div className="max-h-[38vh] overflow-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100 text-left">
                  <tr><th className="p-3">Código</th><th className="p-3">Producto</th><th className="p-3">Cuotas y valores</th><th className="p-3"></th></tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className={`border-t ${selectedProduct?.id === product.id ? "bg-emerald-50" : ""}`}>
                      <td className="p-3">{product.code}</td>
                      <td className="p-3 font-medium">{product.description}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-2">
                          {plansFor(product).map((plan) => (
                            <span key={plan.installments} className="rounded bg-slate-100 px-2 py-1">
                              {plan.label}: {formatARS(plan.total)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3">
                        <button type="button" onClick={() => setSelectedProduct(product)} className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-3 py-2">
                          Seleccionar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedProduct && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="font-semibold text-slate-800">Producto seleccionado</p>
                <p className="text-slate-700">{selectedProduct.description}</p>
                <p className="mt-1 text-sm text-slate-600">
                  El descuento para empleados y el plan de cuotas especial se definirán en una segunda etapa.
                </p>
              </div>
            )}

            <div className="flex-shrink-0">
              <label
                htmlFor="tmc-purchase-observations"
                className="mb-1 block text-sm font-semibold text-slate-700"
              >
                Observaciones opcionales
              </label>
              <textarea
                id="tmc-purchase-observations"
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                rows={3}
                placeholder="Escribí aquí cualquier aclaración sobre la solicitud..."
                className="block min-h-[88px] w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-custom-green-ccard-1"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-3 border-t border-slate-200 pt-4">
          <button type="button" onClick={onClose} className="bg-slate-500 hover:bg-slate-600 text-white font-bold px-4 py-3 rounded-lg">Cancelar</button>
          <button type="submit" disabled={!selectedProduct} className="bg-custom-green-ccard-1 hover:bg-custom-green-ccard-2 disabled:opacity-50 text-white font-bold px-4 py-3 rounded-lg">
            Confirmar solicitud
          </button>
        </div>
      </form>
    </div>
  );
};

export default TmcApplicationForm;
